import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { requestOtp as requestOtpApi, verifyOtp as verifyOtpApi } from '../services/auth';
import { tokenStore } from '../services/api';
import { navigate } from '../lib/router';

const AuthContext = createContext(null);
const USER_KEY = 'travelens_admin_user';

// Session = token (localStorage) + the admin object returned by verify-otp.
// There is no /auth/me, so we restore the user from localStorage.
function loadUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY)) || null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => (tokenStore.get() ? loadUser() : null));
  const [loading] = useState(false); // no async session fetch needed

  // Keep user in sync with token presence on mount.
  useEffect(() => {
    if (!tokenStore.get()) setUser(null);
  }, []);

  // Step 1: send the OTP to the admin email.
  const requestOtp = useCallback((email) => requestOtpApi(email), []);

  // Step 2: verify OTP -> store token + admin, return the admin.
  const verifyOtp = useCallback(async (email, otp) => {
    const { token, admin } = await verifyOtpApi(email, otp);
    tokenStore.set(token);
    localStorage.setItem(USER_KEY, JSON.stringify(admin));
    setUser(admin);
    return admin;
  }, []);

  const signOut = useCallback(() => {
    tokenStore.clear();
    localStorage.removeItem(USER_KEY);
    setUser(null);
    navigate('/login');
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, requestOtp, verifyOtp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
